import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { formatCents, formatDateTime, formatDay } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { inDispute, INVOICE_STATUS, ORDER_SOURCE, ORDER_STATUS, PAYMENT_STATUS, type OrderDetail, type PaymentRow } from "./orders";
import { DisputeBadge, PaidWith } from "./PaymentBits";
import { StripeLink } from "@/components/StripeLink";
import { RefundDialog } from "./RefundDialog";
import { refundBlocked } from "./keapBilled";
import { MoveInstallmentDialog } from "./DateMoveDialogs";
import { useContactNames } from "./useContactNames";
import { DeliveryChecklist, type OrderDelivery } from "./delivery";
import { InvoiceActions, ResolveButton } from "./PaymentFixDialogs";
import { OrderPartner } from "@/features/affiliates/CreditOrderDialog";
import { PrintJobActions } from "./PrintJobActions";

/** One order: what was bought, its invoices, payments, refunds and payment-plan schedule. */
export function OrderDetailPage() {
  const { orderId } = useParams({ from: "/billing/orders/$orderId" });
  const id = Number(orderId);
  const isAdmin = useIsAdmin();
  const q = useQuery({ queryKey: ["order", id], queryFn: () => api<OrderDetail>(`/api/v1/orders/${id}`) });
  const name = useContactNames(q.data ? [q.data.contact_id] : []);
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [moving, setMoving] = useState<{ seq: number; due_date: string; amount_cents: number } | null>(null);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error?.message ?? "Order not found."}</p>;
  const o = q.data;
  const [label, variant] = ORDER_STATUS[o.status] ?? [o.status, "muted" as const];
  const tookMoney = o.payments.some((p) => p.status === "succeeded");
  const canCancel = !tookMoney && ["draft", "pending"].includes(o.status);
  const keapOrder = !!o.keap_id || o.source === "keap_import";
  const subInvoice = (p: PaymentRow) => !!o.invoices.find((iv) => iv.id === p.invoice_id)?.subscription_id;
  return (
    <div className="mx-auto max-w-[1100px] space-y-3">
      <Link to="/billing/orders" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Orders and payments
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="num text-xl font-semibold">Order #{o.id}</h1>
        <BrandMark id={o.brand_id} />
        <Badge variant={variant}>{label}</Badge>
        <span className="text-sm text-[var(--color-muted-foreground)]">{ORDER_SOURCE[o.source] ?? o.source}</span>
        {o.is_test && (
          <Badge variant="warning" title="Left out of sales, payments and money-due numbers">
            test order
          </Badge>
        )}
        <span className="ml-auto flex flex-wrap gap-2">
          <OrderPartner orderId={o.id} affiliateId={o.affiliate_id} keapOrder={keapOrder} buyerId={o.contact_id} />
          <Button size="sm" variant="outline" onClick={() => setTesting(true)}>
            {o.is_test ? "Not a test" : "Mark as test"}
          </Button>
          {canCancel && (
            <Button size="sm" variant="outline" onClick={() => setCancelling(true)}>
              Cancel order
            </Button>
          )}
        </span>
      </div>
      <p className="text-sm">
        <Link to="/contacts/$contactId" params={{ contactId: String(o.contact_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
          {name(o.contact_id)}
        </Link>
        <span className="text-[var(--color-muted-foreground)]">
          , placed {formatDateTime(o.placed_at)}
          {o.paid_at ? `, paid ${formatDateTime(o.paid_at)}` : ""}
          {o.cancelled_at ? `, cancelled ${formatDateTime(o.cancelled_at)}` : ""}
          {o.created_by ? `, by ${o.created_by}` : ""}
        </span>
      </p>
      {o.notes && <p className="text-sm text-[var(--color-muted-foreground)]">{o.notes}</p>}
      {["paid", "partially_paid", "partially_refunded"].includes(o.status) && <DeliveryCard orderId={o.id} />}

      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>What was bought</CardTitle>
        </CardHeader>
        <Table>
          <THead>
            <tr>
              <TH>Item</TH>
              <TH className="text-right">Qty</TH>
              <TH className="text-right">Each</TH>
              <TH className="text-right">Total</TH>
            </tr>
          </THead>
          <tbody>
            {o.items.map((it) => (
              <TR key={it.id}>
                <TD>
                  {it.product_id ? (
                    <Link to="/billing/products/$productId" params={{ productId: String(it.product_id) }} className="hover:underline">
                      {it.description}
                    </Link>
                  ) : (
                    it.description
                  )}
                  {it.discount_cents ? <div className="text-xs text-[var(--color-muted-foreground)]">{formatCents(it.discount_cents)} off</div> : null}
                </TD>
                <TD className="num text-right">{it.quantity}</TD>
                <TD className="num text-right">{formatCents(it.unit_cents)}</TD>
                <TD className="num text-right">{formatCents(it.total_cents)}</TD>
              </TR>
            ))}
            <TR>
              <TD colSpan={3} className="text-right text-[var(--color-muted-foreground)]">
                {[o.discount_cents ? `discount ${formatCents(o.discount_cents)}` : "", o.shipping_cents ? `shipping ${formatCents(o.shipping_cents)}` : "", o.tax_cents ? `tax ${formatCents(o.tax_cents)}` : ""]
                  .filter(Boolean)
                  .join(", ") || "Total"}
              </TD>
              <TD className="num text-right font-semibold">{formatCents(o.total_cents)}</TD>
            </TR>
          </tbody>
        </Table>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <Card className="overflow-x-auto">
          <CardHeader>
            <CardTitle>Invoices</CardTitle>
          </CardHeader>
          <Table>
            <THead>
              <tr>
                <TH>Invoice</TH>
                <TH>Due</TH>
                <TH className="text-right">Owed</TH>
                <TH>Status</TH>
                <TH className="w-px" />
              </tr>
            </THead>
            <tbody>
              {o.invoices.length === 0 && (
                <TR>
                  <TD colSpan={5} className="text-[var(--color-muted-foreground)]">
                    No invoices.
                  </TD>
                </TR>
              )}
              {o.invoices.map((iv) => {
                const [l, v] = INVOICE_STATUS[iv.status] ?? [iv.status, "muted" as const];
                return (
                  <TR key={iv.id}>
                    <TD>
                      <span className="num">{iv.number}</span>
                      {iv.description && <div className="max-w-[14rem] truncate text-xs text-[var(--color-muted-foreground)]">{iv.description}</div>}
                    </TD>
                    <TD className="num text-xs whitespace-nowrap">{formatDay(iv.due_date)}</TD>
                    <TD className="num text-right">{formatCents(iv.amount_due_cents - iv.amount_paid_cents)}</TD>
                    <TD>
                      <Badge variant={v}>{l}</Badge>
                      {iv.void_reason && <div className="text-xs text-[var(--color-muted-foreground)]">{iv.void_reason}</div>}
                    </TD>
                    <TD>
                      <InvoiceActions iv={iv} keapOrder={keapOrder} />
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </Card>
        <Card className="overflow-x-auto">
          <CardHeader>
            <CardTitle>Payments</CardTitle>
            {!isAdmin && o.payments.some((p) => p.status === "succeeded") && <span className="text-xs text-[var(--color-warning-fg)]">Only an admin can refund.</span>}
          </CardHeader>
          <Table>
            <THead>
              <tr>
                <TH>When (MT)</TH>
                <TH className="text-right">Amount</TH>
                <TH>Result</TH>
                {isAdmin && <TH className="w-px" />}
              </tr>
            </THead>
            <tbody>
              {o.payments.length === 0 && (
                <TR>
                  <TD colSpan={4} className="text-[var(--color-muted-foreground)]">
                    No payments yet.
                  </TD>
                </TR>
              )}
              {o.payments.map((p) => {
                const [l, v] = PAYMENT_STATUS[p.status] ?? [p.status, "muted" as const];
                const left = p.amount_cents - p.refunded_cents;
                return (
                  <TR key={p.id}>
                    <TD className="num text-xs whitespace-nowrap">
                      {formatDateTime(p.attempted_at)}
                      <div className="text-[var(--color-muted-foreground)]">
                        <PaidWith p={p} />
                      </div>
                    </TD>
                    <TD className="num text-right">
                      {formatCents(p.amount_cents)}
                      {p.refunded_cents ? <div className="text-xs text-[var(--color-muted-foreground)]">{formatCents(p.refunded_cents)} refunded</div> : null}
                    </TD>
                    <TD>
                      <span className="flex flex-wrap gap-1">
                        <Badge variant={v}>{l}</Badge>
                        <DisputeBadge p={p} />
                      </span>
                      {p.failure_message && <div className="max-w-[14rem] text-xs text-[var(--color-muted-foreground)]">{p.failure_message}</div>}
                    </TD>
                    {isAdmin && (
                      <TD>
                        {p.status === "succeeded" && left > 0 && !inDispute(p) && refundBlocked(p.gateway) && (
                          <span className="text-xs text-[var(--color-muted-foreground)]">{refundBlocked(p.gateway)}</span>
                        )}
                        {p.status === "succeeded" && left > 0 && !inDispute(p) && !refundBlocked(p.gateway) && (
                          <Button size="sm" variant="ghost" onClick={() => setRefunding(p)}>
                            Refund or void
                          </Button>
                        )}
                        {inDispute(p) && <span className="text-xs text-[var(--color-muted-foreground)]">in a chargeback: no refund</span>}
                        {(p.status === "unknown" || p.status === "pending") && <ResolveButton item={{ kind: "payment", id: p.id, amount_cents: p.amount_cents, gateway: p.gateway }} />}
                      </TD>
                    )}
                  </TR>
                );
              })}
            </tbody>
          </Table>
          {o.refunds.length > 0 && (
            <div className="space-y-1 border-t px-4 py-2 text-xs">
              {o.refunds.map((r) => (
                <div key={r.id}>
                  <span className="font-medium">
                    {r.method === "void" ? "Voided" : "Refunded"} {formatCents(r.amount_cents)}
                  </span>{" "}
                  ({r.status}) on {formatDateTime(r.created_at)} by {r.created_by}
                  {r.reason ? `: ${r.reason}` : ""}
                  {r.failure_message ? <span className="text-[var(--color-danger-fg)]"> {r.failure_message}</span> : null}
                  {r.status === "pending" ? (
                    <span className="ml-1 inline-block">
                      <ResolveButton item={{ kind: "refund", id: r.id, amount_cents: r.amount_cents, gateway: o.payments.find((p) => p.id === r.payment_id)?.gateway }} />
                    </span>
                  ) : null}
                  {r.dashboard_url ? (
                    <>
                      {" "}
                      <StripeLink url={r.dashboard_url} what="This refund" />
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {o.schedule.length > 0 && (
        <Card className="overflow-x-auto">
          <CardHeader>
            <CardTitle>Payment plan</CardTitle>
          </CardHeader>
          <Table>
            <THead>
              <tr>
                <TH>Due</TH>
                <TH className="text-right">Amount</TH>
                <TH>Status</TH>
                <TH />
              </tr>
            </THead>
            <tbody>
              {o.schedule.map((sch, i) => {
                const openInvoice = o.invoices.find((iv) => iv.id === sch.invoice_id && iv.status === "open");
                // A payment still to come can move to another date (Brandon, 2026-11-04); Keap plans finish in Keap.
                const canMove =
                  !o.keap_id && sch.source !== "keap_legacy" && sch.seq !== undefined && !["refunded", "cancelled", "void"].includes(o.status) && (sch.status === "scheduled" || (sch.status === "invoiced" && !!openInvoice));
                return (
                  <TR key={sch.id ?? i}>
                    <TD className="num">{formatDay(openInvoice?.due_date ?? sch.due_date)}</TD>
                    <TD className="num text-right">{formatCents(sch.amount_cents)}</TD>
                    <TD className="text-xs">{sch.status.replace(/_/g, " ")}</TD>
                    <TD className="text-right">
                      {canMove && (
                        <Button size="sm" variant="ghost" onClick={() => setMoving({ seq: sch.seq!, due_date: openInvoice?.due_date ?? sch.due_date, amount_cents: sch.amount_cents })}>
                          Move date
                        </Button>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}
      {refunding && <RefundDialog payment={refunding} renewsSubscription={subInvoice(refunding)} onClose={() => setRefunding(null)} />}
      {cancelling && <CancelOrderDialog id={o.id} onClose={() => setCancelling(false)} />}
      {testing && <TestOrderDialog id={o.id} isTest={!!o.is_test} onClose={() => setTesting(false)} />}
      {moving && <MoveInstallmentDialog orderId={o.id} row={moving} onClose={() => setMoving(null)} />}
    </div>
  );
}

/** orders_cancel: only for orders that haven't taken money; voids their open invoices. */
/** Test order on/off (Brandon, 2026-11-04): a real person's test purchase stays as it is but leaves the sales numbers. Moves no money. */
export function TestOrderDialog({ id, isTest, onClose }: { id: number; isTest: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const set = useMutation({
    mutationFn: () => api(`/api/v1/orders/${id}/test`, { json: { is_test: !isTest } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["order", id] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["reports-home"] });
      toast(isTest ? `Order #${id} counts as a sale again` : `Order #${id} marked as a test`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
        <DialogTitle>{isTest ? `Count order #${id} as a sale again?` : `Mark order #${id} as a test?`}</DialogTitle>
        <DialogDescription>
          {isTest
            ? "It goes back into sales, payments and money-due numbers."
            : "The order, its payments and refunds stay exactly as they are; it is only left out of sales, payments and money-due numbers. No money moves."}
        </DialogDescription>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={set.isPending} onClick={() => set.mutate()}>
            {isTest ? "Count it again" : "Mark as test"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelOrderDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const cancel = useMutation({
    mutationFn: () => api(`/api/v1/orders/${id}/cancel`, { json: { reason: reason.trim() } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["order", id] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      toast(`Order #${id} cancelled`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
        <DialogTitle>Cancel order #{id}?</DialogTitle>
        <DialogDescription>It hasn't taken any money. Its open invoices are voided, so no pay link or charge works for them any more.</DialogDescription>
        <div className="space-y-1 text-sm">
          <Label htmlFor="co-reason">Reason (required)</Label>
          <Input id="co-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Changed their mind on the phone" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="destructive" disabled={!reason.trim() || cancel.isPending} onClick={() => cancel.mutate()}>
            Cancel order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** What the paid order promised and where each part stands (billing_order_delivery; read-only). */
function DeliveryCard({ orderId }: { orderId: number }) {
  const q = useQuery({ queryKey: ["order-delivery", orderId], queryFn: () => api<OrderDelivery>(`/api/v1/orders/${orderId}/delivery`) });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Delivery</CardTitle>
        {q.data && (q.data.problems > 0 ? <Badge variant="destructive">{q.data.problems} to fix</Badge> : <Badge variant="success">All delivered or under way</Badge>)}
      </CardHeader>
      <div className="px-4 pb-4">
        {q.isLoading && <Skeleton className="h-16" />}
        {q.error && <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
        {q.data && <DeliveryChecklist steps={q.data.steps} actions={(s) => <PrintJobActions step={s} orderId={orderId} />} />}
      </div>
    </Card>
  );
}
